import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, Mail, Shield, BarChart3, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

export default function AdvisorLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('advisorSavedEmail');
    const rememberMePref = localStorage.getItem('advisorRememberMe');
    if (savedEmail && rememberMePref) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      // Check if user is an advisor
      if (response.data.user.role !== 'advisor') {
        toast.error('This login is for Financial Advisors only. Please use the consumer login.');
        setErrors({ submit: 'Invalid account type' });
        setLoading(false);
        return;
      }
      
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('advisorRememberMe', 'true');
        localStorage.setItem('advisorSavedEmail', email);
      } else {
        localStorage.removeItem('advisorRememberMe');
        localStorage.removeItem('advisorSavedEmail');
      }
      
      toast.success('Welcome back to your practice! 🎯');
      onLogin(response.data.token, response.data.user);
      navigate('/advisor/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-orange-500/10 to-red-500/5 blur-3xl -top-32 -right-32 animate-pulse" style={{ animationDuration: '5s' }} />
        <div className="absolute w-72 h-72 rounded-full bg-gradient-to-r from-purple-500/5 to-pink-500/5 blur-3xl bottom-1/4 -left-20 animate-pulse" style={{ animationDuration: '6s', animationDelay: '2s' }} />
      </div>

      <motion.div 
        className="w-full max-w-md relative z-10"
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
      >
        {/* Back to Landing */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4 }}>
          <Link 
            to="/advisors" 
            className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Advisor Home
          </Link>
        </motion.div>

        {/* Login Card */}
        <motion.div 
          className="bg-gray-800/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl"
          variants={fadeUp}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <motion.div className="mb-8" variants={fadeUp} transition={{ duration: 0.4 }}>
            <div className="flex items-center gap-3 mb-6">
              <motion.div 
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.05, rotate: 3 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <span className="text-white font-bold text-xl">FA</span>
              </motion.div>
              <div>
                <h1 className="text-2xl font-bold text-white">FinAI</h1>
                <p className="text-sm text-gray-400">for Advisors</p>
              </div>
            </div>
            
            <div className="inline-flex items-center space-x-2 bg-orange-900/30 border border-orange-700 px-3 py-1.5 rounded-full mb-4">
              <BarChart3 className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-xs font-semibold text-orange-300">FOR FINANCIAL ADVISORS</span>
            </div>
            
            <h2 className="text-3xl font-bold mb-2 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Welcome back
            </h2>
            <p className="text-gray-400">
              Sign in to manage your practice and clients
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Field */}
            <div>
              <Label htmlFor="email" className="text-gray-300 font-medium flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <div className="relative mt-2">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setErrors(prev => ({ ...prev, email: '' }));
                  }}
                  className={`h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500 ${
                    errors.email ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="advisor@company.com"
                />
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Password Field */}
            <div>
              <Label htmlFor="password" className="text-gray-300 font-medium flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Password
              </Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors(prev => ({ ...prev, password: '' }));
                  }}
                  className={`h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 focus:ring-orange-500 focus:border-orange-500 pr-12 ${
                    errors.password ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                {errors.password && (
                  <p className="text-red-400 text-sm mt-1.5 flex items-center gap-1">
                    <span>⚠</span> {errors.password}
                  </p>
                )}
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-gray-500"
                />
                <label
                  htmlFor="remember"
                  className="text-sm text-gray-300 cursor-pointer select-none"
                >
                  Remember me
                </label>
              </div>
              <Link
                to="/advisor/forgot-password"
                className="text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold h-12 rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 mt-6"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Security Badge */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <Shield className="w-4 h-4" />
            <span>Secure encrypted connection</span>
          </div>

          {/* Sign Up Link */}
          <motion.div className="mt-8 pt-6 border-t border-gray-700" variants={fadeUp} transition={{ duration: 0.4 }}>
            <p className="text-center text-gray-400 mb-4">
              Don't have an advisor account?
            </p>
            <Link to="/advisor/signup" className="block">
              <Button
                variant="outline"
                className="w-full h-12 border-2 border-orange-600 text-orange-400 hover:bg-orange-900/20 font-semibold"
              >
                Request Advisor Access
              </Button>
            </Link>
            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm text-gray-400 hover:text-gray-300 transition-colors inline-block"
              >
                Looking for personal finance? <span className="font-semibold text-orange-400 whitespace-nowrap">Sign in here →</span>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}